import {
  computePosition,
  flip,
  offset,
  shift,
  autoUpdate,
  arrow,
} from '@floating-ui/dom';

let visibleHintsCount = 0;

export default {
  bind(el, binding) {
    initializeHint(el, binding);
  },

  inserted(el, binding) {
    // Re-initialize hint when element is actually inserted into DOM
    initializeHint(el, binding);
  },

  update(el, binding) {
    const { text, enabled = true } = binding.value || {};
    if (!enabled) return;

    if (el._hint) {
      el._hint.updateHintText(text);
    } else {
      // Re-initialize if hint was not created
      initializeHint(el, binding);
    }
  },

  unbind(el) {
    cleanupHint(el);
  },
};

function initializeHint(el, binding) {
  const {
    enabled = true,
    position = 'top',
    yOffset = 5,
    text = 'text',
    showDelay = 300,
    hideDelay = 300,
  } = binding.value || {};

  if (!enabled) return;

  // Cleanup existing hint if any
  cleanupHint(el);

  let instantShowDelay = 250;
  let hintEl = null;
  let arrowEl = null;
  let cleanup = null;
  let showTimeout = null;
  let hideTimeout = null;

  const createHint = () => {
    hintEl = document.createElement('div');
    arrowEl = document.createElement('div');
    arrowEl.className = 'hint-arrow';
    hintEl.className = 'hint';
    hintEl.style.position = 'absolute';
    hintEl.style.opacity = '0';
    hintEl.style.transform = 'scale(0.8)';
    hintEl.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    hintEl.style.zIndex = '9999';

    hintEl.innerText = text;
    hintEl.appendChild(arrowEl);
    document.body.appendChild(hintEl);
  };

  const updateHintText = (newText) => {
    if (hintEl) {
      hintEl.innerText = newText;
      hintEl.appendChild(arrowEl);
    }
  };

  const destroyHint = () => {
    if (hintEl) {
      hintEl.style.opacity = '0';
      hintEl.style.transform = 'scale(0.95)';

      setTimeout(() => {
        if (hintEl && hintEl.parentNode) {
          document.body.removeChild(hintEl);
        }
        hintEl = null;
        arrowEl = null;
      }, 200);
    }
  };

  const updatePosition = () => {
    if (!hintEl || !document.body.contains(el)) return;

    computePosition(el, hintEl, {
      placement: position,
      middleware: [
        offset(yOffset),
        flip(),
        shift({ padding: 10 }),
        arrow({ element: arrowEl }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      if (!hintEl) return;

      hintEl.style.left = `${x}px`;
      hintEl.style.top = `${y}px`;
      hintEl.style.opacity = '1';
      hintEl.style.transform = 'scale(1)';
      hintEl.className = `hint hint--${placement}`;

      let { x: arrowX, y: arrowY } = middlewareData.arrow || {};
      if (arrowX !== undefined) {
        arrowX = Math.max(5, Math.min(arrowX, hintEl.offsetWidth - 5));
        arrowEl.style.left = `${arrowX}px`;
      }
      if (arrowY !== undefined) {
        arrowY = Math.max(5, Math.min(arrowY, hintEl.offsetHeight - 5));
        arrowEl.style.top = `${arrowY}px`;
      }
    });
  };

  const showHint = () => {
    if (!document.body.contains(el)) return;

    if (!hintEl) {
      createHint();
    }
    hintEl.style.transform = 'scale(0.8)';
    hintEl.style.opacity = '0';
    cleanup = autoUpdate(el, hintEl, updatePosition);

    visibleHintsCount += 1;
  };

  const hideHint = () => {
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
    destroyHint();

    visibleHintsCount = Math.max(0, visibleHintsCount - 1);
  };

  const handleMouseEnter = () => {
    clearTimeout(hideTimeout);
    showTimeout = setTimeout(
      () => showHint(),
      visibleHintsCount > 0 ? Math.min(instantShowDelay, showDelay) : showDelay
    );
  };

  const handleMouseLeave = () => {
    clearTimeout(showTimeout);
    hideTimeout = setTimeout(() => hideHint(), hideDelay);
  };

  const handleClick = () => {
    clearTimeout(showTimeout);
    hideHint();
  };

  el.addEventListener('mouseenter', handleMouseEnter);
  el.addEventListener('mouseleave', handleMouseLeave);
  el.addEventListener('click', handleClick);

  el._hint = {
    cleanup,
    showHint,
    hideHint,
    updateHintText,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
  };
}

function cleanupHint(el) {
  if (!el._hint) return;

  const { cleanup, hideHint, handleMouseEnter, handleMouseLeave, handleClick } =
    el._hint;

  if (cleanup) cleanup();
  hideHint();

  el.removeEventListener('mouseenter', handleMouseEnter);
  el.removeEventListener('mouseleave', handleMouseLeave);
  el.removeEventListener('click', handleClick);

  delete el._hint;
}
